import { isNull, uniq } from 'lodash/fp';
import React, { useCallback, useMemo } from 'react';

import { useAppDispatch, useAppSelector } from '../../../hooks/react-redux';
import { useFetchStarted } from '../../../hooks/useFetchStarted';
import { Document } from '../../shared/icons';
import { Spinner } from '../../shared/spinner/Spinner';
import { WizardModal } from '../../shared/wizard/WizardModal';
import { fetchAllClientsStarted } from '../clients/store';
import { ClientDatasetModal } from './ClientDatasetModal';
import { DocumentNameTab, SystemDocumentNameDB, generateReportStarted, getAllAdminDocumentNames, getClientDatasetConfirmationModalOpen, getDocumentNameHasUpdated, getIsGeneratingReport, getSelectedDocumentNameTab, getUnsavedChangeModalOpen, setDocumentNameWizardTab, toggleClientDatasetConfirmationModal, toggleUnsavedChangesModal, updateDocumentNameStarted } from './store';
import { AIDiscover, General, Schedule } from './tabs';
import { UnsavedChangesModal } from '../../shared/modal/UnsavedChangesModal';

interface DocumentNameModalProps {
    isOpen: boolean;
    closeModal: () => void;
    systemDocumentName: SystemDocumentNameDB;
}

export const DocumentNameModal: React.FC<DocumentNameModalProps> = ({ isOpen, closeModal, systemDocumentName }) => {
    const { documentNameId, documentName, datasetId, clientIds, potentialDocumentNames, documentKeyWords, documentExcludedWords, agreementTypeId } = systemDocumentName;
    const modalHeader = documentNameId ? 'Update Document Name' : 'Create a New Document Name';
    const confirmLabel = documentNameId ? 'Update' : 'Create';

    const dispatch = useAppDispatch();
    const documentNames = useAppSelector(getAllAdminDocumentNames);
    const clientDatasetModalOpen = useAppSelector(getClientDatasetConfirmationModalOpen);
    const selectedTab = useAppSelector(getSelectedDocumentNameTab);
    const hasUpdated = useAppSelector(getDocumentNameHasUpdated);
    const unsavedChangesModalOpen = useAppSelector(getUnsavedChangeModalOpen);
    const generatingReport = useAppSelector(getIsGeneratingReport);
    const documentNameTabs = Object.values(DocumentNameTab);
    const selectedTabIndex = documentNameTabs.indexOf(selectedTab);

    const selectTab = useCallback((tab: DocumentNameTab) => dispatch(setDocumentNameWizardTab(tab)), [dispatch]);
    const previous = useCallback(() => selectTab(documentNameTabs[selectedTabIndex - 1]), [documentNameTabs, selectTab, selectedTabIndex]);
    const next = useCallback(() => selectTab(documentNameTabs[selectedTabIndex + 1]), [documentNameTabs, selectTab, selectedTabIndex]);

    const isNewDataset = useMemo(() => !isNull(datasetId) && !uniq(documentNames.filter(({ datasetId }) => datasetId).map(({ datasetId }) => datasetId)).includes(datasetId), [documentNames, datasetId]);
    const emptyDiscoverItems = useMemo(() => [...potentialDocumentNames || [], ...documentKeyWords || [], ...documentExcludedWords || []].some(item => item.length === 0), [potentialDocumentNames, documentKeyWords, documentExcludedWords]);
    const nonUniqueName = useMemo(() => documentNames.filter(({ documentNameId }) => documentNameId !== systemDocumentName.documentNameId).map(({ documentName }) => documentName.toLowerCase()).includes(documentName.toLowerCase()), [documentNames, documentName, systemDocumentName.documentNameId]);
    const confirmDisabled = useMemo(() => !hasUpdated || !documentName.length || emptyDiscoverItems || nonUniqueName, [documentName.length, hasUpdated, emptyDiscoverItems, nonUniqueName]);

    const updateDocumentName = useCallback(() => dispatch(updateDocumentNameStarted()), [dispatch]);
    const confirm = useCallback(() => isNewDataset ? dispatch(toggleClientDatasetConfirmationModal(true)) : updateDocumentName(), [isNewDataset, dispatch, updateDocumentName]);

    const generateReport = useCallback(() => !isNull(agreementTypeId) && !isNull(datasetId) && dispatch(generateReportStarted(agreementTypeId)), [dispatch, datasetId, agreementTypeId]);
    const closeWizard = useCallback(() => hasUpdated ? dispatch(toggleUnsavedChangesModal(true)) : closeModal(), [hasUpdated, dispatch, closeModal]);
    const cancelClose = useCallback(() => dispatch(toggleUnsavedChangesModal(false)), [dispatch]);

    const confirmDisabledTooltip = useMemo(() => {
        let disabledTooltip: string[] = [];
        if (!hasUpdated) {
            disabledTooltip.push('You have not made any changes to this document type.');
        }
        if (!documentName.length || nonUniqueName) {
            disabledTooltip.push('Your document must include a unique document name.');
        }
        if (emptyDiscoverItems) {
            disabledTooltip.push('One or more of the items in your AI Help Info is empty.');
        }
        return disabledTooltip;
    }, [hasUpdated, documentName.length, nonUniqueName, emptyDiscoverItems]);

    const closeClientDatasetModal = () => dispatch(toggleClientDatasetConfirmationModal(false));

    useFetchStarted([fetchAllClientsStarted()]);

    const wizardTabs = useMemo(() => documentNameTabs.map(tab => ({
        label: tab,
        selected: tab === selectedTab,
        onClick: () => selectTab(tab)
    })), [selectTab, selectedTab, documentNameTabs]);

    const wizardButtons = useMemo(() => [
        {
            label: 'Close',
            onClick: closeWizard,
            showButton: true,
            displayLeft: true,
            disabled: false,
            disabledTooltip: null
        },
        {
            label: 'Generate Report',
            onClick: generateReport,
            showButton: !isNull(agreementTypeId) && !isNull(datasetId),
            displayLeft: true,
            disabled: generatingReport,
            disabledTooltip: null
        },
        {
            label: 'Previous',
            onClick: previous,
            disabled: false,
            showButton: selectedTabIndex !== 0,
            disabledTooltip: null,
        },
        {
            label: 'Next',
            onClick: next,
            disabled: false,
            disabledTooltip: null,
            showButton: selectedTabIndex !== documentNameTabs.length - 1
        },
        {
            label: confirmLabel,
            onClick: confirm,
            disabled: confirmDisabled,
            disabledTooltip: confirmDisabledTooltip,
            showButton: true
        }
    ], [selectedTabIndex, documentNameTabs, closeWizard, previous, next, confirm, confirmDisabled, confirmDisabledTooltip, confirmLabel, datasetId, agreementTypeId, generateReport, generatingReport]);

    const tabContent = useMemo(() => {
        switch (selectedTab) {
            case DocumentNameTab.GENERAL:
                return <General systemDocumentName={systemDocumentName} nonUniqueName={!documentName.length || nonUniqueName} />;
            case DocumentNameTab.SCHEDULE:
                return <Schedule systemDocumentName={systemDocumentName} />;
            case DocumentNameTab.AI_DISCOVER:
                return <AIDiscover systemDocumentName={systemDocumentName} />;
            default:
                return <Spinner />;
        }
    }, [selectedTab, systemDocumentName, documentName.length, nonUniqueName]);

    return (
        <>
            <WizardModal
                isOpen={isOpen}
                closeWizard={closeWizard}
                buttons={wizardButtons}
                tabs={wizardTabs}
                headerIcon={Document}
                title={modalHeader}
                testId='document-name'
            >
                {tabContent}
            </WizardModal>
            {clientIds &&
                <ClientDatasetModal
                    isOpen={clientDatasetModalOpen}
                    closeModal={closeClientDatasetModal}
                    updateDocumentName={updateDocumentName}
                    selectedClientIds={clientIds}
                />
            }
            {unsavedChangesModalOpen && <UnsavedChangesModal closeModal={cancelClose} confirm={closeModal} isOpen={unsavedChangesModalOpen} />}
        </>
    );
};
